import { useEffect, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { getToken } from '@/lib/auth'
import AuthForm from '@/components/AuthForm'
import Logo from '@/components/Logo'

// Minimal shape of the externally-connectable chrome API that Chrome injects into pages
// whose origin is listed in the extension manifest's `externally_connectable.matches`.
interface ChromeRelay {
  runtime?: {
    sendMessage(extensionId: string, message: unknown, callback?: (response?: unknown) => void): void
  }
}

// Web sign-in relay for the Chrome extension (Option 1). The extension popup opens this page
// in a tab with `?ext_id=<extension id>`. Google (GIS) sign-in works here because it's a
// normal web origin — unlike the MV3 popup, where the remote GIS script is blocked by CSP.
// On success we hand the JWT to the extension via chrome.runtime.sendMessage; its background
// worker stores it and the popup + suno.com content script pick up the session.
export default function ExtensionAuthPage() {
  const { isAuthenticated } = useAuth()
  const [status, setStatus] = useState<'idle' | 'relayed' | 'no-extension'>('idle')

  useEffect(() => {
    if (!isAuthenticated || status !== 'idle') return
    const token = getToken()
    const extId = new URLSearchParams(window.location.search).get('ext_id')
    const chromeApi = (window as unknown as { chrome?: ChromeRelay }).chrome

    if (token && extId && chromeApi?.runtime?.sendMessage) {
      chromeApi.runtime.sendMessage(extId, { type: 'promptioso-auth', token }, () => {
        // Ignore chrome.runtime.lastError — the web session is valid either way.
      })
      setStatus('relayed')
    } else {
      setStatus('no-extension')
    }
  }, [isAuthenticated, status])

  return (
    <main className="mx-auto flex min-h-screen max-w-md flex-col items-center px-6 py-16">
      <Logo size="lg" showTagline />

      {isAuthenticated ? (
        <div className="mt-10 w-full rounded-2xl border border-gray-800 bg-gray-950 p-6 text-center">
          <div className="text-3xl">✓</div>
          <h1 className="mt-2 text-lg font-semibold text-gray-100">You're signed in</h1>
          {status === 'relayed' ? (
            <p className="mt-2 text-sm text-gray-400">
              Promptioso is connected. Close this tab and open the panel on <strong>suno.com</strong>.
            </p>
          ) : (
            <p className="mt-2 text-sm text-gray-400">
              Signed in on the web. If you opened this from the extension, reopen the Promptioso popup
              to continue.
            </p>
          )}
        </div>
      ) : (
        <div className="mt-8 w-full">
          <p className="text-center text-sm text-gray-400">Sign in to connect the Promptioso extension.</p>
          <AuthForm />
        </div>
      )}
    </main>
  )
}
